import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useZoomTriggerGroupProcessMutation,
  useRunFakturowniaGroupSyncMutation,
  useGroupAppendStudentMutation,
  useMaterialCreateMutation,
  useMaterialUpdateMutation,
  useMaterialDeleteMutation,
  MaterialKindEnum,
  CreateAgreementInputBase,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';

/* ---------- TZ §G9 — group-scoped Zoom processing ---------- */

interface ITriggerZoomGroupProcessOpts {
  dateFrom?: string;
  dateTo?: string;
}

interface ITriggerZoomGroupProcessHook {
  trigger: (opts?: ITriggerZoomGroupProcessOpts) => Promise<void>;
  pending: boolean;
}

/**
 * Wraps `triggerZoomGroupProcess` with the same toast pattern the
 * existing global sync hooks use — fire-and-toast. We don't poll the
 * status here: the matcher writes its progress into the ZoomTab
 * recordings list, which the caller refetches via `onSynced`.
 */
export const useTriggerZoomGroupProcess = (
  groupId: string | undefined,
  onSynced?: () => void,
): ITriggerZoomGroupProcessHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [run] = useZoomTriggerGroupProcessMutation();
  const [pending, setPending] = useState(false);

  const trigger = useCallback(
    async (opts?: ITriggerZoomGroupProcessOpts) => {
      if (!groupId || pending) return;
      setPending(true);
      try {
        const result = await run({
          variables: {
            groupId,
            dateFrom: opts?.dateFrom,
            dateTo: opts?.dateTo,
          },
        });
        const payload = result.data?.triggerZoomGroupProcess;
        if (!payload) return;
        const queuedCount = payload.queuedCount ?? 0;
        const message =
          payload.message ||
          (payload.enqueued
            ? t('pages.groupDetail.sync.zoomQueued', { count: queuedCount })
            : t('pages.groupDetail.sync.zoomCoalesced'));
        addToast({
          type: GlobalToastTypeNames.ZoomSync,
          data: {
            type: payload.enqueued ? ToastTypes.info : ToastTypes.warning,
            title: message,
          },
        });
        if (payload.enqueued) onSynced?.();
      } catch (e) {
        addToast({
          type: GlobalToastTypeNames.ZoomSync,
          data: {
            type: ToastTypes.error,
            title: t('pages.groupDetail.sync.zoomError'),
            text: (e as Error).message,
          },
        });
      } finally {
        setPending(false);
      }
    },
    [groupId, pending, run, addToast, onSynced, t],
  );

  return { trigger, pending };
};

/* ---------- TZ §G10 — group-scoped Fakturownia sync ---------- */

interface ITriggerFakturowniaGroupSyncHook {
  trigger: (period?: string) => Promise<void>;
  pending: boolean;
}

export const useTriggerFakturowniaGroupSync = (
  groupId: string | undefined,
  onSynced?: () => void,
): ITriggerFakturowniaGroupSyncHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [run] = useRunFakturowniaGroupSyncMutation();
  const [pending, setPending] = useState(false);

  const trigger = useCallback(
    async (period?: string) => {
      if (!groupId || pending) return;
      setPending(true);
      try {
        const result = await run({
          variables: { groupId, period: period || 'last_30_days' },
        });
        const payload = result.data?.triggerFakturowniaGroupSync;
        if (!payload) return;
        const emailsTargeted = payload.emailsTargeted ?? 0;
        const message =
          payload.message ||
          (payload.enqueued
            ? t('pages.groupDetail.sync.fakturowniaQueued', {
                count: emailsTargeted,
              })
            : t('pages.groupDetail.sync.fakturowniaCoalesced'));
        addToast({
          type: GlobalToastTypeNames.FakturowniaSync,
          data: {
            type: payload.enqueued ? ToastTypes.info : ToastTypes.warning,
            title: message,
          },
        });
        if (payload.enqueued) onSynced?.();
      } catch (e) {
        addToast({
          type: GlobalToastTypeNames.FakturowniaSync,
          data: {
            type: ToastTypes.error,
            title: t('pages.groupDetail.sync.fakturowniaError'),
            text: (e as Error).message,
          },
        });
      } finally {
        setPending(false);
      }
    },
    [groupId, pending, run, addToast, onSynced, t],
  );

  return { trigger, pending };
};

/* ---------- TZ §G8 — single-shot append student to a group ---------- */

interface IAppendStudentToGroupHook {
  append: (args: {
    studentId: string;
    agreementInput: CreateAgreementInputBase;
  }) => Promise<boolean>;
  pending: boolean;
}

export const useAppendStudentToGroup = (
  onAppended?: () => void,
): IAppendStudentToGroupHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [run] = useGroupAppendStudentMutation();
  const [pending, setPending] = useState(false);

  const append = useCallback(
    async ({
      studentId,
      agreementInput,
    }: {
      studentId: string;
      agreementInput: CreateAgreementInputBase;
    }) => {
      if (pending) return false;
      setPending(true);
      try {
        const result = await run({
          variables: { studentId, agreementInput },
        });
        const payload = result.data?.appendStudentToGroup;
        if (payload?.success && payload.agreement) {
          addToast({
            type: GlobalToastTypeNames.StudentAgreementAdd,
            data: {
              type: ToastTypes.success,
              title: t('pages.groupDetail.appendStudent.toastSuccess'),
            },
          });
          onAppended?.();
          return true;
        }
        const errMsg =
          payload?.customErrors?.[0]?.message ||
          payload?.errors ||
          t('pages.groupDetail.appendStudent.toastError');
        addToast({
          type: GlobalToastTypeNames.StudentAgreementAdd,
          data: {
            type: ToastTypes.error,
            title: t('pages.groupDetail.appendStudent.toastError'),
            text: String(errMsg),
          },
        });
        return false;
      } catch (e) {
        addToast({
          type: GlobalToastTypeNames.StudentAgreementAdd,
          data: {
            type: ToastTypes.error,
            title: t('pages.groupDetail.appendStudent.toastError'),
            text: (e as Error).message,
          },
        });
        return false;
      } finally {
        setPending(false);
      }
    },
    [pending, run, addToast, onAppended, t],
  );

  return { append, pending };
};

/* ---------- TZ §G6 — Materials CRUD ---------- */

interface IMaterialMutationsHook {
  create: (input: {
    groupId: string;
    lessonId: string;
    kind: MaterialKindEnum;
    title: string;
    description?: string;
    dueDate?: string;
  }) => Promise<boolean>;
  update: (
    id: string,
    input: {
      lessonId?: string;
      kind?: MaterialKindEnum;
      title?: string;
      description?: string;
      dueDate?: string;
    },
  ) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  pending: boolean;
}

export const useMaterialMutations = (
  onChanged?: () => void,
): IMaterialMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [createRun] = useMaterialCreateMutation();
  const [updateRun] = useMaterialUpdateMutation();
  const [deleteRun] = useMaterialDeleteMutation();
  const [pending, setPending] = useState(false);

  const toastErr = useCallback(
    (key: string, text?: string) => {
      addToast({
        type: GlobalToastTypeNames.StudentAgreementAdd,
        data: { type: ToastTypes.error, title: t(key), text },
      });
    },
    [addToast, t],
  );

  const create: IMaterialMutationsHook['create'] = useCallback(
    async (input) => {
      setPending(true);
      try {
        const result = await createRun({ variables: { input } });
        if (result.data?.createMaterial?.success) {
          onChanged?.();
          return true;
        }
        toastErr(
          'pages.groupDetail.materials.toastCreateError',
          result.data?.createMaterial?.customErrors?.[0]?.message || undefined,
        );
        return false;
      } catch (e) {
        toastErr(
          'pages.groupDetail.materials.toastCreateError',
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [createRun, onChanged, toastErr],
  );

  const update: IMaterialMutationsHook['update'] = useCallback(
    async (id, input) => {
      setPending(true);
      try {
        const result = await updateRun({ variables: { id, input } });
        if (result.data?.updateMaterial?.success) {
          onChanged?.();
          return true;
        }
        toastErr(
          'pages.groupDetail.materials.toastUpdateError',
          result.data?.updateMaterial?.customErrors?.[0]?.message || undefined,
        );
        return false;
      } catch (e) {
        toastErr(
          'pages.groupDetail.materials.toastUpdateError',
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [updateRun, onChanged, toastErr],
  );

  const remove: IMaterialMutationsHook['remove'] = useCallback(
    async (id) => {
      setPending(true);
      try {
        const result = await deleteRun({ variables: { id } });
        if (result.data?.deleteMaterial?.success) {
          onChanged?.();
          return true;
        }
        toastErr(
          'pages.groupDetail.materials.toastDeleteError',
          result.data?.deleteMaterial?.customErrors?.[0]?.message || undefined,
        );
        return false;
      } catch (e) {
        toastErr(
          'pages.groupDetail.materials.toastDeleteError',
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [deleteRun, onChanged, toastErr],
  );

  return { create, update, remove, pending };
};
